import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import tinycolor from 'tinycolor2';
import { RadioButtonChecked, RadioButtonUnchecked, Close, TuneOutlined, ChevronRight, Speed, ChevronLeft } from '@material-ui/icons';
import { Dialog, Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { setOpenSettingMenu } from '../../actions/configurationActions';
import { joinClassNames } from '../../util/util';

const AUTO_RESOLUTION = { value: 'Auto', label: 'Auto' };

const PLAYBACK_SPEEDS = [
	{ value: 0.25, label: '0.25' },
	{ value: 0.5, label: '0.5' },
	{ value: 0.75, label: '0.75' },
	{ value: 1, label: '1' },
	{ value: 1.5, label: '1.5' },
	{ value: 1.75, label: '1.75' },
	{ value: 2, label: '2' },
];
interface QualitySettingMenuProps {
	playerConfigs?: any;
	isMobile?: boolean;
	sideWindowOpen?: boolean;
	className?: string;
}

const useListCustomStyle = makeStyles(() => ({
	listItem: {
		minWidth: '190px',
	},
	listItemFocus: {
		outline: '1px dash white',
	},
	listItemIcon: {
		fontSize: '1rem',
	},
	primaryListText: {
		fontSize: '0.75rem',
		lineHeight: 1.5,
		fontFamily: 'inherit',
	},
	focusButton: {
		'&:focus': {
			color: '#ffffff',
		},
	},
}));

export const QualitySettingMenu: React.FC<QualitySettingMenuProps> = ({ playerConfigs, sideWindowOpen, className, isMobile }) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { openSettingMenu, mediasData, currentMediaId, playlistId, isMediaLive, mediaType } = useSelector(
		(state) => (state as any).configurationReducer
	);

	const [selectedValue, setSelectedValue] = useState(AUTO_RESOLUTION.value);
	const [selectedPlaybackSpeed, setSelectedPlaybackSpeed] = useState('1');
	const [hoveringItem, setHoveringItem] = useState('');

	const [selectedMenu, setSelectedMenu] = useState('');
	const [selectedMenuHoveringItem, setSelectedMenuHoveringItem] = useState('');

	const playerId = useMemo(() => `player-${playlistId || currentMediaId}`, [playlistId, currentMediaId]);
	const color = useMemo(() => playerConfigs?.playerStyling?.primaryColor ?? '#126cfc', [playerConfigs?.playerStyling?.primaryColor]);
	const opacityN = useMemo(() => Number(playerConfigs?.playerStyling?.primaryOpacity ?? 100), [playerConfigs?.playerStyling?.primaryOpacity]);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacityN / 100 };
	const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };
	const classes = useListCustomStyle();

	const menuItems = useMemo(() => {
		const currentMedia = mediasData.find(({ id, originalId }: any) => id === currentMediaId || id === originalId) ?? mediasData?.[0];
		const stream = ((currentMedia?.mediaDetails ?? [])[0]?.streams ?? []).find((s: any) => s.videos.length > 0);
		if (!stream || stream.videos.length === 0) {
			return [];
		}
		return stream.videos;
	}, [currentMediaId, mediasData]);

	const onCloseMenu = useCallback(() => {
		dispatch(setOpenSettingMenu(null));
		setHoveringItem('');
		setSelectedMenu('');
	}, [dispatch]);

	const changeVideoQuality = useCallback(
		(item: any) => {
			if (item.label === selectedValue) {
				return;
			}
			setSelectedValue(item.label);
			(window as any).GoBrain.widgets(playerId).quality(item);
		},
		[selectedValue, playerId]
	);

	const changePlaybackSpeed = useCallback(
		(item: any) => {
			if (item.label === selectedValue) {
				return;
			}
			setSelectedPlaybackSpeed(item.label);
			(window as any).GoBrain.widgets(playerId).playbackRate(item.value);
		},
		[selectedValue, playerId]
	);

	const onMenuItemClick = useCallback(
		(_e: React.MouseEvent<HTMLElement>, item: any, type = 'quality') => {
			if (type === 'quality') {
				changeVideoQuality(item);
			} else {
				changePlaybackSpeed(item);
			}
		},
		[changeVideoQuality, changePlaybackSpeed]
	);

	const onKeyDown = useCallback(
		(event, item?: any, type = 'quality') => {
			if (event.key === 'Tab') {
				event.stopPropagation();
				return;
			}

			if (event.key === 'Enter' || event.key === ' ') {
				if (type === 'quality') {
					changeVideoQuality(item);
				} else {
					changePlaybackSpeed(item);
				}
				event.stopPropagation();
			}
		},
		[changeVideoQuality, changePlaybackSpeed]
	);

	const handleKeydownInSettingMenu = (event: any, type: any) => {
		if (event.key === 'Tab') {
			event.stopPropagation();
			return;
		}
		if (event.key === 'Enter' || event.key === ' ') {
			setSelectedMenu(type);
		}
	};

	useEffect(() => {
		if (openSettingMenu?.type === 'quality') {
			return;
		}
		return () => {
			setHoveringItem('');
			setSelectedMenu('');
		};
	}, [openSettingMenu]);

	const renderSettingMenuItems = () => {
		const listItemClass = classes.listItem;
		const showPlaybackSpeedSettings = playerConfigs.bottomControls?.showPlaybackSpeedSettings && !isMediaLive && mediaType !== 'scenario';
		const showBothMenus = playerConfigs.bottomControls?.showVideoQualitySettings && menuItems.length > 0 && showPlaybackSpeedSettings;

		return (
			<>
				{!selectedMenu && showBothMenus && (
					<>
						{isMobile && (
							<>
								<List>
									<ListItem tabIndex={0} className={listItemClass}>
										<ListItemText classes={{ primary: classes.primaryListText }} primary={t('SETTINGS')} />
										<IconButton
											aria-label={t('LABEL_CLOSE_MEDIA_LIST')}
											style={{ minWidth: '30px', marginLeft: 'auto', height: '32px', width: '32px' }}
											onClick={onCloseMenu}
											className={classes.focusButton}
										>
											<Close className={classes.listItemIcon} htmlColor="#fff" />
										</IconButton>
									</ListItem>
								</List>

								<Divider
									tabIndex={-1}
									aria-hidden="true"
									style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', margin: '0 5px 3px' }}
								/>
							</>
						)}
						<List className={isMobile ? 'qplayer__setting-menu--mobile-scrollable' : ''} id="sub-header">
							<ListItem
								className={listItemClass}
								tabIndex={0}
								onClick={() => {
									setSelectedMenu('playbackSpeed');
								}}
								onKeyDown={(e) => {
									handleKeydownInSettingMenu(e, 'playbackSpeed');
								}}
								aria-haspopup="true"
								style={selectedMenuHoveringItem === 'playbackSpeed' ? hoverStyle : undefined}
								onMouseOver={() => setSelectedMenuHoveringItem('playbackSpeed')}
								onMouseLeave={() => setSelectedMenuHoveringItem('')}
								onFocus={() => setSelectedMenuHoveringItem('playbackSpeed')}
								onBlur={() => setSelectedMenuHoveringItem('')}
							>
								<ListItemIcon style={{ minWidth: '30px' }}>
									<Speed className={classes.listItemIcon} htmlColor="#fff" />
								</ListItemIcon>
								<ListItemText classes={{ primary: classes.primaryListText }} primary={t('PLAYBACK_SPEED')} />
								<IconButton
									tabIndex={-1}
									aria-hidden={true}
									style={{ minWidth: '30px', marginLeft: 'auto', height: '32px', width: '32px' }}
								>
									<ChevronRight className={classes.listItemIcon} htmlColor="#fff" />
								</IconButton>
							</ListItem>

							{menuItems.length > 0 && (
								<ListItem
									className={listItemClass}
									tabIndex={0}
									onClick={() => {
										setSelectedMenu('quality');
									}}
									onKeyDown={(e) => {
										handleKeydownInSettingMenu(e, 'quality');
									}}
									aria-haspopup="true"
									style={selectedMenuHoveringItem === 'quality' ? hoverStyle : undefined}
									onMouseOver={() => setSelectedMenuHoveringItem('quality')}
									onMouseLeave={() => setSelectedMenuHoveringItem('')}
									onFocus={() => setSelectedMenuHoveringItem('quality')}
									onBlur={() => setSelectedMenuHoveringItem('')}
								>
									<ListItemIcon style={{ minWidth: '30px' }}>
										<TuneOutlined className={classes.listItemIcon} htmlColor="#fff" />
									</ListItemIcon>
									<ListItemText classes={{ primary: classes.primaryListText }} primary={t('QUALITY')} />
									<IconButton
										tabIndex={-1}
										aria-hidden={true}
										style={{ minWidth: '30px', marginLeft: 'auto', height: '32px', width: '32px' }}
									>
										<ChevronRight className={classes.listItemIcon} htmlColor="#fff" />
									</IconButton>
								</ListItem>
							)}
						</List>
					</>
				)}

				{(selectedMenu === 'playbackSpeed' ||
					(showPlaybackSpeedSettings && (!playerConfigs.bottomControls?.showVideoQualitySettings || menuItems.length === 0))) && (
					<>
						<ListItem
							ContainerProps={{
								role: showBothMenus ? 'button' : 'title',
								'aria-label': showBothMenus ? t('LABEL_GO_BACK_BTN') : undefined,
							}}
							tabIndex={showBothMenus ? 0 : -1}
							className={listItemClass}
							onClick={showBothMenus ? () => setSelectedMenu('') : undefined}
							onKeyDown={showBothMenus ? (e: any) => handleKeydownInSettingMenu(e, '') : undefined}
							style={hoveringItem === 'playbackSpeed-back' ? hoverStyle : undefined}
							onMouseOver={showBothMenus ? () => setHoveringItem('playbackSpeed-back') : undefined}
							onMouseLeave={showBothMenus ? () => setHoveringItem('') : undefined}
							onFocus={showBothMenus ? () => setHoveringItem('playbackSpeed-back') : undefined}
							onBlur={showBothMenus ? () => setHoveringItem('') : undefined}
						>
							<ListItemIcon style={{ minWidth: '30px' }}>
								{showBothMenus ? (
									<ChevronLeft className={classes.listItemIcon} htmlColor="#fff" />
								) : (
									<Speed className={classes.listItemIcon} htmlColor="#fff" />
								)}
							</ListItemIcon>
							<ListItemText classes={{ primary: classes.primaryListText }} primary={t('PLAYBACK_SPEED')} id="sub-header" />
							{!showBothMenus && isMobile && (
								<IconButton
									aria-label={t('LABEL_CLOSE_MEDIA_LIST')}
									style={{ minWidth: '30px', marginLeft: 'auto', height: '32px', width: '32px' }}
									onClick={onCloseMenu}
									className={classes.focusButton}
								>
									<Close className={classes.listItemIcon} htmlColor="#fff" />
								</IconButton>
							)}
						</ListItem>
						<Divider aria-hidden="true" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', margin: '1px' }} />

						<List
							className={joinClassNames('qplayer__language-panel', isMobile ? 'qplayer__setting-menu--mobile-scrollable' : '')}
							onKeyDown={(e: any) => onKeyDown(e, {})}
							role="menu"
						>
							{PLAYBACK_SPEEDS.map((item: any, index: number) => {
								const isSelected = selectedPlaybackSpeed === item.label;
								const isHovering = hoveringItem === item.label;

								return (
									<ListItem
										className={listItemClass}
										onKeyDown={(e) => onKeyDown(e, item, 'playbackSpeed')}
										tabIndex={0}
										key={`${item.value}-${index}`}
										value={item.value}
										onClick={(e) => onMenuItemClick(e, item, 'playbackSpeed')}
										style={isHovering ? hoverStyle : undefined}
										onMouseOver={() => setHoveringItem(item.label)}
										onMouseLeave={() => setHoveringItem('')}
										onFocus={() => setHoveringItem(item.label)}
										onBlur={() => setHoveringItem('')}
										role="menuitemradio"
										aria-checked={isSelected ? 'true' : 'false'}
									>
										<ListItemIcon style={{ minWidth: '30px' }}>
											{isSelected ? (
												<RadioButtonChecked className={classes.listItemIcon} htmlColor="#fff" />
											) : (
												<RadioButtonUnchecked className={classes.listItemIcon} htmlColor="#fff" />
											)}
										</ListItemIcon>
										<ListItemText
											classes={{ primary: classes.primaryListText }}
											primary={item.label === '1' ? t('PLAYBACK_SPEED_NORMAL') : item.label}
										/>
									</ListItem>
								);
							})}
						</List>
					</>
				)}

				{(selectedMenu === 'quality' || !showPlaybackSpeedSettings) && (
					<>
						<ListItem
							ContainerProps={{
								role: showBothMenus ? 'button' : 'title',
								'aria-label': showBothMenus ? t('LABEL_GO_BACK_BTN') : undefined,
							}}
							tabIndex={showBothMenus ? 0 : -1}
							className={listItemClass}
							onClick={showBothMenus ? () => setSelectedMenu('') : undefined}
							onKeyDown={showBothMenus ? (e: any) => handleKeydownInSettingMenu(e, '') : undefined}
							style={hoveringItem === 'quality-back' ? hoverStyle : undefined}
							onMouseOver={showBothMenus ? () => setHoveringItem('quality-back') : undefined}
							onMouseLeave={showBothMenus ? () => setHoveringItem('') : undefined}
							onFocus={showBothMenus ? () => setHoveringItem('quality-back') : undefined}
							onBlur={showBothMenus ? () => setHoveringItem('') : undefined}
						>
							<ListItemIcon style={{ minWidth: '30px' }}>
								{showBothMenus ? (
									<ChevronLeft className={classes.listItemIcon} htmlColor="#fff" />
								) : (
									<TuneOutlined className={classes.listItemIcon} htmlColor="#fff" />
								)}
							</ListItemIcon>
							<ListItemText classes={{ primary: classes.primaryListText }} primary={t('QUALITY')} id="sub-header" />
							{!showBothMenus && isMobile && (
								<IconButton
									aria-label={t('LABEL_CLOSE_MEDIA_LIST')}
									style={{ minWidth: '30px', marginLeft: 'auto', height: '32px', width: '32px' }}
									onClick={onCloseMenu}
									className={classes.focusButton}
								>
									<Close className={classes.listItemIcon} htmlColor="#fff" />
								</IconButton>
							)}
						</ListItem>
						<Divider aria-hidden="true" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', margin: '1px' }} />

						<List
							className={joinClassNames('subtitle-panel', isMobile ? 'qplayer__setting-menu--mobile-scrollable' : '')}
							onKeyDown={(e: any) => onKeyDown(e, {})}
							role="menu"
						>
							{menuItems.map((item: any, index: number) => {
								if (!item) {
									return null;
								}

								const isSelected = selectedValue === item.label;
								const isHovering = hoveringItem === item.label;

								return (
									<ListItem
										className={listItemClass}
										onKeyDown={(e) => onKeyDown(e, item, 'quality')}
										tabIndex={0}
										key={`${item.label}-${index}`}
										value={item.label}
										onClick={(e) => onMenuItemClick(e, item, 'quality')}
										style={isHovering ? hoverStyle : undefined}
										onMouseOver={() => setHoveringItem(item.label)}
										onMouseLeave={() => setHoveringItem('')}
										onFocus={() => setHoveringItem(item.label)}
										onBlur={() => setHoveringItem('')}
										role="menuitemradio"
										aria-checked={isSelected ? 'true' : 'false'}
									>
										<ListItemIcon style={{ minWidth: '30px' }}>
											{isSelected ? (
												<RadioButtonChecked className={classes.listItemIcon} htmlColor="#fff" />
											) : (
												<RadioButtonUnchecked className={classes.listItemIcon} htmlColor="#fff" />
											)}
										</ListItemIcon>
										<ListItemText classes={{ primary: classes.primaryListText }} primary={item.label} />
									</ListItem>
								);
							})}
						</List>
					</>
				)}
			</>
		);
	};

	if (isMobile) {
		return (
			<Dialog
				className="qplayer__setting-menu"
				open={openSettingMenu?.type === 'quality'}
				style={{ zIndex: '99000', width: sideWindowOpen ? '75%' : '100%', backgroundColor: '#000000' }}
				onClose={onCloseMenu}
				fullWidth
				maxWidth="lg"
			>
				<div className="qplayer__setting-menu--mobile" onMouseDown={(e) => e.stopPropagation()}>
					{renderSettingMenuItems()}
				</div>
			</Dialog>
		);
	}

	return openSettingMenu?.type === 'quality' ? (
		<Menu
			onMouseDown={(e) => e.stopPropagation()}
			style={{ zIndex: '99000', position: 'static' }}
			key={'quality-menu'}
			className={joinClassNames('qplayer__setting-menu', className)}
			open={openSettingMenu?.type === 'quality'}
			onClose={onCloseMenu}
			getContentAnchorEl={null}
			anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
			transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			anchorEl={openSettingMenu?.anchor || document.querySelector('.bottomControlbar')}
			tabIndex={0}
			PaperProps={{
				style: openSettingMenu?.anchor ? { transform: 'translateY(-30px)' } : {},
				className: 'closeable-by-tab',
			}}
			MenuListProps={{
				'aria-labelledby': 'sub-header',
			}}
		>
			<div tabIndex={-1}>{renderSettingMenuItems()}</div>
		</Menu>
	) : null;
};
